import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, ArrowRight, CircuitBoard, LogOut } from "lucide-react";

const RULES = [
  { test: (p) => p.length >= 8, label: "Mínimo 8 caracteres" },
  { test: (p) => /[a-z]/.test(p), label: "Al menos una minúscula" },
  { test: (p) => /[A-Z]/.test(p), label: "Al menos una mayúscula" },
  { test: (p) => /[0-9]/.test(p), label: "Al menos un número" },
  { test: (p) => /[^a-zA-Z0-9]/.test(p), label: "Símbolo especial (recomendado)", optional: true },
];

export default function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr("Las contraseñas no coinciden");
      return;
    }
    const failing = RULES.filter((r) => !r.optional && !r.test(next));
    if (failing.length) {
      setErr(failing.map((r) => r.label).join(" · "));
      return;
    }
    setLoading(true);
    const r = await changePassword(current, next);
    setLoading(false);
    if (r.ok) navigate("/");
    else setErr(r.error || "Error cambiando contraseña");
  };

  const forced = user.must_change_password;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md ds-card ds-hero-glow" style={{ boxShadow: "var(--shadow-lg)" }}>
        <div className="ds-card-h">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 grid place-items-center rounded-lg text-white font-display font-black"
              style={{ background: "linear-gradient(160deg,#6E8B3D,#8AAF4E)", boxShadow: "inset 0 -2px 0 rgba(0,0,0,.15),0 1px 2px rgba(15,18,16,.08)" }}
            >
              <CircuitBoard size={22} strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
                {forced ? "// Cambio obligatorio" : "// Cambiar contraseña"}
              </div>
              <div className="font-display font-black text-xl tracking-tight">Nueva contraseña</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-3">
          {forced && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
              Tu administrador te creó una contraseña temporal. Define una nueva contraseña para continuar.
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
              Contraseña actual
            </label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
              data-testid="cp-current"
              className="mt-1 ds-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
              Nueva contraseña
            </label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="cp-new"
              className="mt-1 ds-input"
            />
            <ul className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px]">
              {RULES.map((r, i) => {
                const ok = r.test(next);
                return (
                  <li key={i} className={ok ? "text-emerald-700" : "text-[#8A9098]"}>
                    <span className="inline-block w-3">{ok ? "✓" : r.optional ? "·" : "○"}</span> {r.label}
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
              Repite la nueva contraseña
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              data-testid="cp-confirm"
              className="mt-1 ds-input"
            />
          </div>

          {err && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg font-mono" data-testid="cp-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="cp-submit"
            className="ds-btn primary w-full justify-center"
            style={{ padding: "11px 16px" }}
          >
            <Lock size={14} /> {loading ? "Guardando..." : "Cambiar contraseña"} <ArrowRight size={14} />
          </button>

          {forced && (
            <button
              type="button"
              onClick={() => { logout(); navigate("/login"); }}
              className="w-full text-xs text-[#4B5158] hover:text-red-700 underline underline-offset-4 inline-flex items-center justify-center gap-1"
            >
              <LogOut size={11} /> Cerrar sesión
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
